import React, { useRef, useEffect, useState } from 'react';
import { MARKUP_COORD_SPACE, fitScale, toPagePoint, toPageLength } from './markupGeometry';

/**
 * Simple, lightweight annotation component for one page of a PDF or an image.
 * No complex state management - just draws strokes on a canvas overlay.
 *
 * Strokes live in page space (see markupGeometry.js). The page is fitted into
 * the container at `scale`; pointer positions are divided by it on the way in
 * and the overlay is drawn through a transform of the same scale, so the
 * annotations stay put when the window size changes.
 */
function SimplePdfAnnotator({
    source,
    pageNumber,
    strokes = [],
    onStrokeAdded,
    onStrokesChanged,
    currentTool = 'pen',
    toolSettings = { color: '#000000', thickness: 2, opacity: 1 },
    disabled = false
}) {
    const containerRef = useRef(null);
    const pdfCanvasRef = useRef(null);
    const strokeCanvasRef = useRef(null);
    const previousRenderRef = useRef(null);
    const scaleRef = useRef(1);
    const strokesRef = useRef(strokes);
    const currentStrokeRef = useRef(null); // The stroke being drawn; a ref so pointer moves don't re-render
    const isDrawingRef = useRef(false);
    const redrawFrameRef = useRef(null);
    const [containerSize, setContainerSize] = useState(null);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0, scale: 1 });

    strokesRef.current = strokes;

    // Helper function to check if a point (in page space) is over a stroke
    const isPointOverStroke = (pos, stroke, eraserSize) => {
        if (!stroke.points || stroke.points.length === 0) return false;

        for (const point of stroke.points) {
            const dx = point.x - pos.x;
            const dy = point.y - pos.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < eraserSize) {
                return true;
            }
        }
        return false;
    };

    // Follow the size of the container so the page is refitted when the window changes
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        let timer = null;
        const measure = () => {
            const next = { width: container.clientWidth, height: container.clientHeight };
            if (next.width <= 0 || next.height <= 0) return;
            setContainerSize(prev => (
                prev && prev.width === next.width && prev.height === next.height ? prev : next
            ));
        };

        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(measure, 150);
        });
        observer.observe(container);
        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, []);

    // Render the page
    useEffect(() => {
        if (!source || !containerSize) return undefined;

        let cancelled = false;
        let handle = null;

        const renderPage = async () => {
            // A cancelled render has to let go of the canvas before the next one starts
            if (previousRenderRef.current) {
                await previousRenderRef.current;
            }
            if (cancelled) return;

            const pageSize = await source.getPageSize(pageNumber);
            if (cancelled) return;

            const scale = fitScale(pageSize, containerSize);
            const width = Math.floor(pageSize.width * scale);
            const height = Math.floor(pageSize.height * scale);

            const canvas = pdfCanvasRef.current;
            const strokeCanvas = strokeCanvasRef.current;
            canvas.width = width;
            canvas.height = height;
            strokeCanvas.width = width;
            strokeCanvas.height = height;

            scaleRef.current = scale;
            setCanvasSize({ width, height, scale });

            handle = source.renderPage(pageNumber, canvas, scale);
            const finished = await handle.promise;
            if (!finished || cancelled) return;

            // Redraw strokes after the page renders
            redrawStrokes();
        };

        previousRenderRef.current = renderPage().catch((error) => {
            console.error('Page render error:', error);
        });

        // Cleanup: cancel render on unmount or when dependencies change
        return () => {
            cancelled = true;
            if (handle) handle.cancel();
        };
    }, [source, pageNumber, containerSize]);

    // Redraw all strokes whenever they change
    useEffect(() => {
        redrawStrokes();
    }, [strokes, canvasSize]);

    useEffect(() => () => {
        if (redrawFrameRef.current) cancelAnimationFrame(redrawFrameRef.current);
    }, []);

    const redrawStrokes = () => {
        const canvas = strokeCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const scale = scaleRef.current;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Page space to screen: scales the points and the thickness alike
        ctx.setTransform(scale, 0, 0, scale, 0, 0);

        strokesRef.current.forEach(stroke => {
            drawStroke(ctx, stroke);
        });
        if (currentStrokeRef.current) {
            drawStroke(ctx, currentStrokeRef.current);
        }
    };

    const drawStroke = (ctx, stroke) => {
        if (!stroke.points || stroke.points.length < 2) return;

        ctx.strokeStyle = stroke.style?.color || '#000000';
        ctx.lineWidth = stroke.style?.thickness || 2;
        ctx.globalAlpha = stroke.style?.opacity || 1;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.beginPath();
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);

        for (let i = 1; i < stroke.points.length; i++) {
            ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }

        ctx.stroke();
        ctx.globalAlpha = 1;
    };

    const scheduleRedraw = () => {
        if (redrawFrameRef.current) return;
        redrawFrameRef.current = requestAnimationFrame(() => {
            redrawFrameRef.current = null;
            redrawStrokes();
        });
    };

    // Mouse position in page space
    const getMousePos = (e) => {
        const canvas = strokeCanvasRef.current;
        const rect = canvas.getBoundingClientRect();
        return toPagePoint({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        }, scaleRef.current);
    };

    const eraseAt = (pos) => {
        // Find and remove stroke under eraser
        const eraserSize = toPageLength(toolSettings.thickness * 2, scaleRef.current);
        const strokeIndex = strokes.findIndex(stroke =>
            isPointOverStroke(pos, stroke, eraserSize)
        );

        if (strokeIndex !== -1 && onStrokesChanged) {
            const updatedStrokes = strokes.filter((_, idx) => idx !== strokeIndex);
            onStrokesChanged(updatedStrokes);
        }
    };

    const handleMouseDown = (e) => {
        if (disabled || !canvasSize.width) return;
        const pos = getMousePos(e);

        isDrawingRef.current = true;
        if (currentTool === 'eraser') {
            eraseAt(pos); // Dragging keeps erasing
        } else {
            currentStrokeRef.current = {
                id: `stroke_${Date.now()}_${Math.random()}`,
                tool: currentTool,
                coord_space: MARKUP_COORD_SPACE,
                style: {
                    color: toolSettings.color,
                    thickness: toPageLength(toolSettings.thickness, scaleRef.current),
                    opacity: toolSettings.opacity
                },
                points: [pos],
                created_at: new Date().toISOString()
            };
        }
    };

    const handleMouseMove = (e) => {
        if (!isDrawingRef.current) return;

        const pos = getMousePos(e);

        if (currentTool === 'eraser') {
            eraseAt(pos);
            return;
        }

        const stroke = currentStrokeRef.current;
        if (!stroke) return;

        const lastPoint = stroke.points[stroke.points.length - 1];
        stroke.points.push(pos);

        if (stroke.style.opacity < 1) {
            // Overlapping segments of a see-through stroke would darken at
            // every joint, so the whole stroke is redrawn as one path.
            scheduleRedraw();
            return;
        }

        // Draw current stroke in real-time
        const ctx = strokeCanvasRef.current.getContext('2d');
        ctx.strokeStyle = stroke.style.color;
        ctx.lineWidth = stroke.style.thickness;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
    };

    const handleMouseUp = () => {
        const stroke = currentStrokeRef.current;
        isDrawingRef.current = false;
        currentStrokeRef.current = null;

        if (stroke && stroke.points.length > 1) {
            onStrokeAdded(stroke);
        }
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5'
            }}
        >
            <div style={{
                position: 'relative',
                width: canvasSize.width,
                height: canvasSize.height
            }}>
                {/* Page layer */}
                <canvas
                    ref={pdfCanvasRef}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        background: 'white'
                    }}
                />

                {/* Stroke overlay */}
                <canvas
                    ref={strokeCanvasRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        cursor: currentTool === 'eraser' ? 'grab' : (currentTool === 'pen' || currentTool === 'highlighter' ? 'crosshair' : 'default')
                    }}
                />
            </div>
        </div>
    );
}

export default SimplePdfAnnotator;
